/**
 * identityService.js
 * Acquires the Azure AD Object ID (OID) for the current user.
 *
 * Strategy (NAA — Nested App Authentication):
 *   1. Check Office.context.requirements for NestedAppAuth 1.1 support
 *   2. Use createNestablePublicClientApplication — Office acts as the broker
 *   3. Try acquireTokenSilent (no UI, preferred path)
 *   4. On InteractionRequiredAuthError → acquireTokenPopup (Office-managed popup)
 *   5. Extract OID from result.account.idTokenClaims.oid
 *
 * Rules:
 *   - OID is the sole identity — never falls back to email or displayName
 *   - Returns { oid: string, error: null } on success
 *   - Returns { oid: null, error: string } on failure
 */

import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { msalConfig, msalScopes } from "./msalConfig";

/* global Office, OfficeRuntime */

const DIALOG_AUTH_SUCCESS = "AUTH_SUCCESS";
const DIALOG_AUTH_ERROR = "AUTH_ERROR";

const POPUP_BLOCKED_ERROR_CODES = new Set([
  "popup_window_error",
  "empty_window_error",
  "monitor_popup_timeout",
  "block_nested_popups",
]);

const POPUP_FALLBACK_ERROR_CODES = new Set([
  "interaction_required",
  "login_required",
  "consent_required",
  "no_account_error",
  "no_account_found",
  "user_login_error",
  "native_connection_not_established",
  "platform_broker_error",
  "nested_app_auth_bridge_disabled",
]);

function getOfficeLoginHint() {
  try {
    const email = Office?.context?.mailbox?.userProfile?.emailAddress?.trim();
    return email || undefined;
  } catch {
    return undefined;
  }
}

function getRedirectUri() {
  try {
    return `${window.location.origin}/redirect.html`;
  } catch {
    return undefined;
  }
}

function isMacOutlookHost() {
  try {
    return Office?.context?.platform === Office?.PlatformType?.Mac;
  } catch {
    return false;
  }
}

function getErrorCode(error) {
  return (
    error?.errorCode ||
    error?.code ||
    error?.subError ||
    ""
  ).toString().trim();
}

function getErrorDetails(error) {
  return {
    name: error?.name || "",
    errorCode: error?.errorCode || error?.code || "",
    subError: error?.subError || "",
    message: error?.message || "",
  };
}

function pickBestAccount(accounts, loginHint) {
  if (!Array.isArray(accounts) || accounts.length === 0) {
    return null;
  }

  const normalizedHint = loginHint?.trim().toLowerCase();
  if (normalizedHint) {
    const exactMatch = accounts.find((account) => {
      const candidates = [
        account?.username,
        account?.name,
        account?.idTokenClaims?.preferred_username,
        account?.idTokenClaims?.email,
        account?.idTokenClaims?.upn,
        account?.loginHint,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());

      return candidates.includes(normalizedHint);
    });

    if (exactMatch) {
      return exactMatch;
    }
  }

  return accounts[0];
}

async function recoverOidFromCachedAccount(pca, tokenRequest, loginHint) {
  const accounts = pca.getAllAccounts();
  console.warn(
    "[identityService] Popup recovery: accounts in cache after popup failure:",
    accounts.map((account) => ({
      username: account?.username || "",
      homeAccountId: account?.homeAccountId || "",
      localAccountId: account?.localAccountId || "",
      oid: account?.idTokenClaims?.oid || "",
    }))
  );

  const account = pickBestAccount(accounts, loginHint);
  if (!account) {
    return null;
  }

  if (account?.idTokenClaims?.oid) {
    console.warn("[identityService] Popup recovery: using cached account OID.");
    return account.idTokenClaims.oid;
  }

  try {
    const recoveryResult = await pca.ssoSilent({
      ...tokenRequest,
      account,
      loginHint: loginHint || account.username || undefined,
    });
    const recoveredOid = recoveryResult?.account?.idTokenClaims?.oid;
    if (recoveredOid) {
      console.warn("[identityService] Popup recovery: silent retry succeeded.");
      return recoveredOid;
    }
  } catch (recoveryError) {
    console.error(
      "[identityService] Popup recovery failed:",
      getErrorDetails(recoveryError),
      recoveryError
    );
  }

  return null;
}

function buildLoginRequest(tokenRequest) {
  return {
    ...tokenRequest,
    prompt: "select_account",
  };
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") {
    return null;
  }

  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

async function tryLegacyOfficeSsoOid() {
  if (
    typeof OfficeRuntime === "undefined" ||
    !OfficeRuntime?.auth?.getAccessToken
  ) {
    console.warn("[identityService] OfficeRuntime.auth.getAccessToken not available.");
    return null;
  }

  try {
    const token = await OfficeRuntime.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
    });
    const claims = decodeJwtPayload(token);
    const oid = claims?.oid || null;

    console.warn("[identityService] Legacy Office SSO fallback succeeded:", {
      hasToken: Boolean(token),
      oid,
      preferred_username: claims?.preferred_username || "",
      tid: claims?.tid || "",
    });

    return oid;
  } catch (legacyError) {
    console.error(
      "[identityService] Legacy Office SSO fallback failed:",
      getErrorDetails(legacyError),
      legacyError
    );
    return null;
  }
}

function openOfficeDialog(url) {
  return new Promise((resolve, reject) => {
    if (
      typeof Office === "undefined" ||
      !Office?.context?.ui?.displayDialogAsync
    ) {
      reject(new Error("Office dialog API unavailable"));
      return;
    }

    Office.context.ui.displayDialogAsync(
      url,
      { height: 75, width: 40, displayInIframe: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          reject(asyncResult.error);
          return;
        }

        resolve(asyncResult.value);
      }
    );
  });
}

async function tryOfficeDialogAuth(loginHint) {
  if (
    typeof Office === "undefined" ||
    !Office?.context?.ui?.displayDialogAsync
  ) {
    console.warn("[identityService] Office dialog auth fallback unavailable.");
    return null;
  }

  const dialogUrl = `${window.location.origin}/auth-dialog.html${
    loginHint ? `?loginHint=${encodeURIComponent(loginHint)}` : ""
  }`;
  console.warn("[identityService] Starting Office dialog auth fallback:", dialogUrl);

  try {
    const dialog = await openOfficeDialog(dialogUrl);

    return await new Promise((resolve) => {
      let settled = false;

      const finish = (oid) => {
        if (settled) {
          return;
        }
        settled = true;
        try {
          dialog.close();
        } catch {
          // Ignore close errors.
        }
        resolve(oid || null);
      };

      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        (arg) => {
          try {
            const data = JSON.parse(arg.message);
            if (data?.type === DIALOG_AUTH_SUCCESS && data?.oid) {
              console.warn("[identityService] Office dialog auth fallback succeeded.");
              finish(data.oid);
              return;
            }

            if (data?.type === DIALOG_AUTH_ERROR) {
              console.error(
                "[identityService] Office dialog auth fallback failed:",
                data?.details || data?.message || data
              );
              finish(null);
            }
          } catch (parseError) {
            console.error("[identityService] Failed to parse dialog auth message:", parseError);
            finish(null);
          }
        }
      );

      dialog.addEventHandler(
        Office.EventType.DialogEventReceived,
        (arg) => {
          console.error("[identityService] Office dialog auth dialog event:", arg);
          finish(null);
        }
      );
    });
  } catch (dialogError) {
    console.error(
      "[identityService] Office dialog auth fallback could not start:",
      dialogError
    );
    return null;
  }
}

function shouldFallbackToPopup(error, isNaaSupported) {
  if (!isNaaSupported) {
    return true;
  }

  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }

  const errorCode = getErrorCode(error);
  if (POPUP_FALLBACK_ERROR_CODES.has(errorCode)) {
    return true;
  }

  if (error?.name === "ServerError") {
    return true;
  }

  const message = error?.message?.toLowerCase?.() || "";
  return (
    message.includes("interaction_required") ||
    message.includes("login required") ||
    message.includes("requires interaction") ||
    message.includes("no account") ||
    message.includes("login_hint") ||
    message.includes("prompt=none") ||
    message.includes("user login is required")
  );
}

function getPopupFailureMessage(error) {
  const errorCode = getErrorCode(error);
  const message = error?.message || "";

  if (
    POPUP_BLOCKED_ERROR_CODES.has(errorCode) ||
    message.includes("popup_window_error")
  ) {
    return "Das Anmeldefenster wurde blockiert. Bitte erlauben Sie Popups für Outlook und versuchen Sie es erneut.";
  }

  if (errorCode === "user_cancelled" || errorCode === "user_canceled") {
    return "Die Anmeldung wurde abgebrochen. Bitte versuchen Sie es erneut.";
  }

  return "Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.";
}

/**
 * Acquires the Azure AD OID for the current user via NAA.
 *
 * @returns {Promise<{ oid: string|null, error: string|null }>}
 */
export async function acquireOid() {
  const isNaaSupported =
    typeof Office !== "undefined" &&
    Office.context?.requirements?.isSetSupported("NestedAppAuth", "1.1");
  const isMacHost = isMacOutlookHost();
  console.log("[identityService] NestedAppAuth 1.1 supported:", isNaaSupported);
  console.log("[identityService] Running on Outlook Mac host:", isMacHost);

  let pca;
  try {
    pca = await createNestablePublicClientApplication(msalConfig);
  } catch (e) {
    console.error("[identityService] MSAL initialization failed:", e);
    return {
      oid: null,
      error: "Authentifizierungsdienst konnte nicht initialisiert werden.",
    };
  }

  const loginHint = getOfficeLoginHint();
  const redirectUri = getRedirectUri();
  const tokenRequest = {
    scopes: msalScopes,
    ...(loginHint ? { loginHint } : {}),
    ...(redirectUri ? { redirectUri } : {}),
  };

  if (loginHint) {
    console.log("[identityService] Using mailbox email as loginHint.");
  }
  if (redirectUri) {
    console.log("[identityService] Using redirectUri:", redirectUri);
  }

  let result;
  try {
    // Phase 1: ssoSilent — works even when no account is in the cache.
    // In Desktop hosts, providing loginHint avoids "no account" silent failures.
    result = await pca.ssoSilent(tokenRequest);
    console.log("[identityService] Token acquired via ssoSilent.");
  } catch (ssoError) {
    if (!shouldFallbackToPopup(ssoError, isNaaSupported)) {
      console.error(
        "[identityService] ssoSilent failed:",
        getErrorDetails(ssoError),
        ssoError
      );
      return {
        oid: null,
        error: "Authentifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.",
      };
    }

    // Phase 2: Interaction required or host-specific silent auth failure — popup fallback
    console.warn(
      "[identityService] Falling back to popup after silent auth failure:",
      getErrorDetails(ssoError)
    );

    if (isMacHost) {
      console.warn(
        "[identityService] Skipping MSAL popup on Mac host and using Office dialog auth directly."
      );

      const dialogOid = await tryOfficeDialogAuth(loginHint);
      if (dialogOid) {
        return { oid: dialogOid, error: null };
      }

      return {
        oid: null,
        error: getPopupFailureMessage(ssoError),
      };
    }

    try {
      const loginResult = await pca.loginPopup(buildLoginRequest(tokenRequest));
      if (loginResult?.account) {
        try {
          pca.setActiveAccount(loginResult.account);
        } catch {
          // Non-fatal: active account is optional for our OID-only use case.
        }
      }
      result = loginResult;
      console.log("[identityService] User signed in via NAA popup.");
    } catch (popupError) {
      console.error(
        "[identityService] Popup failed:",
        getErrorDetails(popupError),
        popupError
      );

      const recoveredOid = await recoverOidFromCachedAccount(
        pca,
        tokenRequest,
        loginHint
      );
      if (recoveredOid) {
        return { oid: recoveredOid, error: null };
      }

      const legacyOid = await tryLegacyOfficeSsoOid();
      if (legacyOid) {
        return { oid: legacyOid, error: null };
      }

      const dialogOid = await tryOfficeDialogAuth(loginHint);
      if (dialogOid) {
        return { oid: dialogOid, error: null };
      }

      return {
        oid: null,
        error: getPopupFailureMessage(popupError),
      };
    }
  }

  const oid = result?.account?.idTokenClaims?.oid;
  if (!oid) {
    console.error("[identityService] OID missing in token claims.", result?.account?.idTokenClaims);
    return {
      oid: null,
      error: "Benutzer-ID konnte nicht gelesen werden. Bitte wenden Sie sich an den Support.",
    };
  }

  return { oid, error: null };
}
